import angular from 'angular'
import api, { AbstractCollectionService } from 'components/api'
import { DROP_SIDES } from '../../shared/drop-position'

class SceneCollectionItemService extends AbstractCollectionService {
  constructor ($q, $log, ApiService, StateService) {
    'ngInject'

    super($q, ApiService, StateService, 'SceneCollectionItem', 'scenecollectionitem')
    this.$log = $log
    this.copiedSceneCollectionId = null
    this.currentMediaStats = {}
    this.mediaStatsListeners = []
    this._pendingRequests = {}

    // Subscribe to through-model changes for immediate UI updates
    ApiService.subscribeToModel('scenecollection_scenecollectionitem', {}, this.sceneCollectionSceneCollectionItemSubscriber.bind(this))
    ApiService.subscribeToModel('scenecollection_scene', {}, this.sceneCollectionSceneSubscriber.bind(this))

    // Subscribe to media label relationship changes to refresh scene collection items when labels change
    ApiService.subscribeToModel('medialabel_filemedia', {}, this.mediaLabelRelationSubscriber.bind(this))
    ApiService.subscribeToModel('medialabel_serviceannouncement', {}, this.mediaLabelRelationSubscriber.bind(this))
    ApiService.subscribeToModel('medialabel_socialmediasource', {}, this.mediaLabelRelationSubscriber.bind(this))

    // Subscribe to MediaLabel changes (for color/name updates)
    ApiService.subscribeToModel('medialabel', {}, this.mediaLabelSubscriber.bind(this))
  }

  // Specifies an increment, we should update all the scenecollectionitems we have
  sceneCollectionSceneCollectionItemSubscriber (error, change) {
    if (!error && change.sceneCollectionItemId) {
      // Force refresh by clearing current state and notifying subscribers
      // This will trigger UI components to refetch current data
      const newState = {}
      newState[this.collectionStateName] = [] // Empty array to force refresh
      this.StateService._setState(newState)
    }
  }

  // Handle scenecollection_scene through-model changes (when collections are added/removed from scenes)
  sceneCollectionSceneSubscriber (error, change) {
    if (!error) {
      if (change && change.new) {
        // Force immediate refresh without debouncing for NEW relationships
        this.StateService.forceNotify()
      } else if (change && change.removed) {
        // Force immediate refresh without debouncing for REMOVED relationships
        this.StateService.forceNotify()
      } else if (change) {
        // Regular update - can use debounced refresh
        this.StateService._setState({ [this.collectionStateName]: [] })
      }
    }
  }

  // Handle media label relationship changes (when labels are added/removed from media)
  mediaLabelRelationSubscriber (error, change) {
    if (!error && change) {
      this.$log.debug('[SceneCollectionItemService] Media label relationship changed:', change)
      // Force refresh of scene collection items to show updated labels
      // This will trigger the getSceneCollectionItemsForSceneCollection call which includes labels
      const newState = {}
      newState[this.collectionStateName] = [] // Empty array to force refresh
      this.StateService._setState(newState)
    }
  }

  // Handle MediaLabel changes (when label name/color is updated)
  mediaLabelSubscriber (error, change) {
    if (!error && change && !change.new && !change.removed) {
      this.$log.debug('[SceneCollectionItemService] MediaLabel updated:', change)
      // Force refresh to show updated label names/colors
      const newState = {}
      newState[this.collectionStateName] = [] // Empty array to force refresh
      this.StateService._setState(newState)
    }
  }

  getSceneCollectionItemsForSceneCollection (sceneCollectionId) {
    this.$log.debug('[SceneCollectionItemService] API call: scenecollectionitem/getSceneCollectionItems', { sceneCollectionId })

    // Cache the last requested sceneCollectionId to avoid duplicate calls
    const cacheKey = `getSceneCollectionItems_${sceneCollectionId}`

    // If we already have a pending request for this sceneCollectionId, return that promise
    if (this._pendingRequests && this._pendingRequests[cacheKey]) {
      this.$log.debug('[SceneCollectionItemService] Reusing pending request for sceneCollectionId:', sceneCollectionId)
      return this._pendingRequests[cacheKey]
    }

    // Initialize pending requests tracker if not exists
    if (!this._pendingRequests) {
      this._pendingRequests = {}
    }

    // Create and cache the promise
    const promise = this.ApiService.call('scenecollectionitem/getSceneCollectionItems', { sceneCollectionId })
      .then(response => {
        // Store media stats for potential future use
        if (response && response.mediaStats) {
          this.currentMediaStats = response.mediaStats
          this.$log.debug('[SceneCollectionItemService] Updated media stats:', this.currentMediaStats)
          // Notify all media stats listeners
          this._notifyMediaStatsListeners(this.currentMediaStats)
        }
        return response
      })
      .finally(() => {
        // Clean up the pending request after completion
        delete this._pendingRequests[cacheKey]
      })

    // Cache the promise
    this._pendingRequests[cacheKey] = promise

    return promise
  }

  getMediaStatsForSceneCollection (sceneCollectionId) {
    // Only return stats if they match the requested scene collection
    if (this.currentSceneCollectionId === sceneCollectionId) {
      return this.currentMediaStats
    }
    return {}
  }

  subscribeToMediaStats (callback) {
    this.mediaStatsListeners.push(callback)

    // Immediately call with current stats if available
    if (Object.keys(this.currentMediaStats).length > 0) {
      callback(this.currentMediaStats, this.currentSceneCollectionId)
    }

    // Return unsubscribe function
    return () => {
      const index = this.mediaStatsListeners.indexOf(callback)
      if (index !== -1) {
        this.mediaStatsListeners.splice(index, 1)
      }
    }
  }

  _notifyMediaStatsListeners (mediaStats) {
    this.mediaStatsListeners.forEach(listener => {
      try {
        listener(mediaStats)
      } catch (error) {
        this.$log.error('[SceneCollectionItemService] Error notifying media stats listener:', error)
      }
    })
  }

  _refreshMediaStatsAfterChange () {
    // Only refresh if we have a current scene collection context
    if (!this.currentSceneCollectionId) {
      return
    }

    // Calculate fresh media stats from current local state - filtered to current scene collection
    const allItems = this.getAll()
    const freshStats = {}

    allItems.forEach(item => {
      if (item.mediaType === 'file' && item.mediaId && item.sceneCollectionId === this.currentSceneCollectionId) {
        freshStats[item.mediaId] = (freshStats[item.mediaId] || 0) + 1
      }
    })

    this.currentMediaStats = freshStats
    this.$log.debug('[SceneCollectionItemService] Refreshed media stats:', this.currentMediaStats)
    this._notifyMediaStatsListeners(this.currentMediaStats)
  }

  /**
   * Move one item of a playlist next to another.
   *
   * `side` says which side of the target the item lands on, and the server
   * places it there. It is required: the server refuses a call without it,
   * because the order of the stored list is not enough to say which side the
   * operator saw.
   *
   * @param {number} sceneCollectionId - The playlist
   * @param {number} sourceItemId - The item that moves
   * @param {number} targetItemId - The item it lands next to
   * @param {string} side - 'before' or 'after'
   * @returns {Promise} The updated row
   */
  reorderSceneCollectionItems (sceneCollectionId, sourceItemId, targetItemId, side) {
    if (!DROP_SIDES.includes(side)) {
      return this.$q.reject(new Error(
        `reorderSceneCollectionItems needs a side of ${DROP_SIDES.join(' or ')}`
      ))
    }

    const payload = { sceneCollectionId, sourceItemId, targetItemId, side }

    this.$log.debug('[SceneCollectionItemService] API call: scenecollectionitem/reorderSceneCollectionItems', payload)
    return this.ApiService.call('scenecollectionitem/reorderSceneCollectionItems', payload)
  }

  setCopiedSceneCollectionId (sceneCollectionId) {
    this.$log.info('setCopiedSceneCollectionId before', this.copiedSceneCollectionId)

    this.copiedSceneCollectionId = sceneCollectionId
    this.$log.info('setCopiedSceneCollectionId after', this.copiedSceneCollectionId)
  }

  getCopiedSceneCollectionId () {
    return this.copiedSceneCollectionId
  }

  clearCopiedSceneCollectionId () {
    this.copiedSceneCollectionId = null
  }

  copyItemsToSceneCollection (sourceSceneCollectionId, targetSceneCollectionId) {
    this.$log.info('copyItemsToSceneCollection', sourceSceneCollectionId, targetSceneCollectionId)
    return this.ApiService.call('scenecollectionitem/copySceneCollectionItems', {
      sourceSceneCollectionId,
      targetSceneCollectionId
    })
  }

  deleteAllItemsInPlaylist (sceneCollectionId) {
    this.$log.info('deleteAllItemsInPlaylist for sceneCollectionId:', sceneCollectionId)
    return this.ApiService.call('scenecollectionitem/deleteAllSceneCollectionItems', {
      sceneCollectionId
    })
  }
}

export default angular.module('scenecollectionitem.service', [
  api
]).service('SceneCollectionItemService', SceneCollectionItemService).name

export { SceneCollectionItemService }
